import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { normalizeExerciseName } from "../../utils/exerciseName";
import {
  getWorkoutDraftSnapshotHash,
  loadWorkoutDraft,
  saveWorkoutDraft,
  withWorkoutDraftMeta,
} from "./workoutDraftStore";
import { logWriteTrace } from "../../utils/writeTrace";
import { isBodyweightExerciseName } from "../../utils/aiWorkoutPlan";

const normalizeDateKey = (value) => String(value || "").slice(0, 10);

// ユーザー保存値がなければ種目名でデフォルト単位を決める。
// exerciseUnits[name] が存在する場合はそちらを優先する（ユーザーの選択が勝つ）。
export const getDefaultUnitForExercise = (name, exerciseUnits = {}) =>
  exerciseUnits[name] || (isBodyweightExerciseName(name) ? "BW" : "kg");

const normalizeWeightMode = (value) => {
  const unit = String(value || "kg").toLowerCase();
  if (unit === "lbs" || unit === "lb" || unit === "pound" || unit === "pounds") return "lbs";
  if (unit === "bw" || unit === "bodyweight" || unit === "自重") return "BW";
  return "kg";
};

const getDisplayUnitForMode = (mode) => {
  const normalized = normalizeWeightMode(mode);
  if (normalized === "lbs") return "lb";
  return normalized;
};

const getSetWeightMode = (set, fallbackUnit = "kg") =>
  normalizeWeightMode(
    set?.weightMode
    || set?.weightType
    || set?.displayUnit
    || set?.unit
    || set?.weightUnit
    || set?.weight_unit
    || fallbackUnit
  );

const withWeightMode = (set, mode) => {
  const normalizedMode = normalizeWeightMode(mode);
  return {
    ...set,
    weightMode: normalizedMode,
    weightType: normalizedMode,
    unit: normalizedMode,
    displayUnit: getDisplayUnitForMode(normalizedMode),
    weightUnit: normalizedMode,
    weight_unit: normalizedMode,
  };
};

const formatWeightValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "";
  const rounded = Math.round(num * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const makeSet = (set = {}, fallbackUnit = "kg") => {
  const mode = getSetWeightMode(set, fallbackUnit);
  const hasWeight = Object.prototype.hasOwnProperty.call(set || {}, "weight");
  const weight = mode === "BW"
    ? (hasWeight && set.weight !== "" ? set.weight : "BW")
    : (hasWeight ? set.weight : "");
  const reps = Object.prototype.hasOwnProperty.call(set || {}, "reps") ? set.reps : "";

  return withWeightMode({
    ...set,
    weight,
    reps,
    done: Boolean(set?.done),
  }, mode);
};

const makeDefaultSets = (fallbackUnit = "kg") => ([
  makeSet({}, fallbackUnit),
  makeSet({}, fallbackUnit),
  makeSet({}, fallbackUnit),
]);

const makeExercise = (exercise = {}, {
  logData = {},
  exerciseUnits = {},
  fallbackLabel = "その他",
} = {}) => {
  const name = String(exercise.name || exercise.exerciseName || "").trim();
  const bodyPart = String(
    exercise.bodyPart
    || exercise.body_part
    || exercise.label
    || fallbackLabel
    || ""
  ).trim();
  const fallbackUnit = getDefaultUnitForExercise(name, exerciseUnits);
  const setsSource = Array.isArray(exercise.sets) && exercise.sets.length
    ? exercise.sets
    : (Array.isArray(logData[name]) && logData[name].length ? logData[name] : null);

  return {
    ...exercise,
    id: exercise.id || name || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    name,
    label: exercise.label || bodyPart || fallbackLabel,
    bodyPart: bodyPart || exercise.label || fallbackLabel,
    sets: setsSource ? setsSource.map((set) => makeSet(set, fallbackUnit)) : makeDefaultSets(fallbackUnit),
  };
};

const getExerciseNames = (exercises = []) => exercises.map((exercise) => exercise.name).filter(Boolean);

const getTotalSetCount = (exercises = []) =>
  (exercises || []).reduce((sum, exercise) => sum + (exercise?.sets || []).length, 0);

const getRemovedExerciseNames = (before = [], after = []) => {
  const afterNames = new Set(after.map((exercise) => normalizeExerciseName(exercise.name)).filter(Boolean));
  return before
    .map((exercise) => exercise.name)
    .filter((name) => {
      const normalized = normalizeExerciseName(name);
      return normalized && !afterNames.has(normalized);
    });
};

const hasDraftContent = (draft = {}) => (
  (draft.exercises || []).length > 0
  || (draft.sessionEx || []).length > 0
  || Object.keys(draft.logData || {}).length > 0
);

const buildExercisesFromDraft = (initialDraft = {}) => {
  const logData = initialDraft.logData || {};
  const exerciseUnits = initialDraft.exerciseUnits || {};
  const labels = Array.isArray(initialDraft.todayLabels) ? initialDraft.todayLabels : [];
  const fallbackLabel = labels[0] || "その他";
  const sourceExercises = Array.isArray(initialDraft.exercises) && initialDraft.exercises.length
    ? initialDraft.exercises
    : (Array.isArray(initialDraft.sessionEx) ? initialDraft.sessionEx : []);
  const merged = [];
  const seen = new Set();

  sourceExercises.forEach((exercise) => {
    const nextExercise = makeExercise(exercise, { logData, exerciseUnits, fallbackLabel });
    const normalized = normalizeExerciseName(nextExercise.name);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    merged.push(nextExercise);
  });

  Object.keys(logData || {}).forEach((exerciseName) => {
    const normalized = normalizeExerciseName(exerciseName);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    merged.push(makeExercise({
      id: exerciseName,
      name: exerciseName,
      label: fallbackLabel,
      bodyPart: fallbackLabel,
    }, { logData, exerciseUnits, fallbackLabel }));
  });

  return merged;
};

const splitExercisesForApp = (exercises = [], previousExerciseUnits = {}) => {
  const logData = {};
  const sessionEx = [];
  const exerciseUnits = { ...(previousExerciseUnits || {}) };

  (exercises || []).forEach((exercise) => {
    const name = String(exercise?.name || "").trim();
    if (!name) return;
    const sets = (exercise.sets || []).map((set) => makeSet(set, exerciseUnits[name] || "kg"));
    logData[name] = sets;
    const firstSetUnit = sets[0]
      ? getSetWeightMode(sets[0], exerciseUnits[name] || "kg")
      : exerciseUnits[name];
    if (firstSetUnit) exerciseUnits[name] = firstSetUnit;
    sessionEx.push({
      ...exercise,
      sets: undefined,
    });
  });

  return { logData, sessionEx, exerciseUnits };
};

const createInitialDraft = (date, initialDraft = {}) => {
  const normalizedDate = normalizeDateKey(date);
  const exercises = buildExercisesFromDraft(initialDraft);
  const split = splitExercisesForApp(exercises, initialDraft.exerciseUnits || {});

  return withWorkoutDraftMeta(normalizedDate, {
    todayLabels: initialDraft.todayLabels || [],
    exercises,
    logData: split.logData,
    sessionEx: split.sessionEx,
    exerciseUnits: split.exerciseUnits,
  }, {
    source: initialDraft.meta?.source || "initial",
    hasUnsavedChanges: Boolean(initialDraft.meta?.hasUnsavedChanges),
    remoteVerifiedAt: initialDraft.meta?.remoteVerifiedAt || "",
  });
};

const updateSetForWeightMode = (set, nextMode, currentMode) => {
  const target = set || { weight: "", reps: "", done: false };
  const normalizedNextMode = normalizeWeightMode(nextMode);
  let nextSet = { ...target };

  if (normalizedNextMode === "BW") {
    const rawWeight = String(target.weight ?? "").trim();
    const numericWeight = Number(rawWeight);
    const hasWeightedValue =
      rawWeight
      && rawWeight.toUpperCase() !== "BW"
      && Number.isFinite(numericWeight)
      && numericWeight > 0;

    nextSet = {
      ...nextSet,
      weight: "BW",
      lastWeightedValue: hasWeightedValue ? formatWeightValue(numericWeight) : target.lastWeightedValue,
      lastWeightedUnit: hasWeightedValue ? currentMode : target.lastWeightedUnit || currentMode,
    };
  } else if (normalizeWeightMode(currentMode) === "BW") {
    nextSet = {
      ...nextSet,
      weight: "",
    };
  } else {
    const displayWeight = String(target.weight ?? "").trim();
    nextSet = {
      ...nextSet,
      weight: displayWeight || "",
      lastWeightedValue: displayWeight || target.lastWeightedValue,
      lastWeightedUnit: displayWeight ? normalizedNextMode : target.lastWeightedUnit,
    };
  }

  nextSet = withWeightMode(nextSet, normalizedNextMode);
  if (nextSet.weight === "BW") {
    nextSet.done = Boolean(nextSet.reps);
  } else if (nextSet.weight || nextSet.reps) {
    nextSet.done = Boolean(nextSet.weight && nextSet.reps);
  }
  return nextSet;
};

export function useWorkoutLog({
  date,
  initialDraft = null,
  storage,
  logger = console,
  debug = false,
  debounceMs = 350,
  onDraftChange,
  onDraftPersist,
  bodyWeightKg = null,
} = {}) {
  const normalizedDate = normalizeDateKey(date);
  const externalDraft = useMemo(() => {
    const normalizedInitialDraft = createInitialDraft(normalizedDate, initialDraft || {});
    if (hasDraftContent(normalizedInitialDraft)) return normalizedInitialDraft;
    const storedDraft = loadWorkoutDraft(normalizedDate, { storage, logger });
    return createInitialDraft(normalizedDate, storedDraft || {});
  }, [initialDraft, logger, normalizedDate, storage]);

  const [draft, setDraft] = useState(() => externalDraft);
  const lastMutationRef = useRef(null);
  const lastNotifiedHashRef = useRef("");
  const lastSavedHashRef = useRef("");
  const bodyWeightKgRef = useRef(bodyWeightKg);
  bodyWeightKgRef.current = bodyWeightKg;

  useEffect(() => {
    const externalHash = getWorkoutDraftSnapshotHash(externalDraft);
    setDraft((prevDraft) => {
      const prevDate = normalizeDateKey(prevDraft?.date || prevDraft?.meta?.date);
      const prevHash = getWorkoutDraftSnapshotHash(prevDraft);
      if (prevDate === normalizedDate && prevHash === externalHash) return prevDraft;
      if (prevDate === normalizedDate && prevDraft?.meta?.hasUnsavedChanges && externalDraft?.meta?.source !== "explicit_date_nav") return prevDraft;
      lastMutationRef.current = null;
      return externalDraft;
    });
  }, [externalDraft, normalizedDate]);

  const draftHash = useMemo(() => getWorkoutDraftSnapshotHash(draft), [draft]);
  const splitDraft = useMemo(
    () => splitExercisesForApp(draft.exercises || [], draft.exerciseUnits || {}),
    [draft.exercises, draft.exerciseUnits]
  );

  useEffect(() => {
    const mutation = lastMutationRef.current;
    if (!mutation) return;
    if (lastNotifiedHashRef.current === draftHash) return;
    lastNotifiedHashRef.current = draftHash;

    onDraftChange?.({
      ...draft,
      ...splitDraft,
    }, mutation);
    lastMutationRef.current = null;
  }, [draft, draftHash, onDraftChange, splitDraft]);

  useEffect(() => {
    if (!draft?.meta?.hasUnsavedChanges) return undefined;
    if (lastSavedHashRef.current === draftHash) return undefined;

    // Stale-closure guard: draft.date doesn't match normalizedDate → old content would be
    // written to the new date's key. Skip until the draft state catches up.
    const draftDate = normalizeDateKey(draft?.date || draft?.meta?.date);
    if (draftDate && draftDate !== normalizedDate) {
      logWriteTrace("G7:stale-date-persist", {
        date: normalizedDate,
        decision: "block",
        reason: "draft.date does not match normalizedDate",
        flags: { explicitEdit: draft?.meta?.explicitEdit, explicitDelete: draft?.meta?.explicitDelete, source: draft?.meta?.source },
        exerciseCount: (draft.exercises || []).length,
        setCount: null,
        extra: { draftDate, normalizedDate },
      });
      logger?.warn?.("[workout log] blocked stale-date persist", {
        action: "stale_closure_write_blocked",
        draftDate,
        normalizedDate,
        source: draft?.meta?.source || null,
      });
      return undefined;
    }
    logWriteTrace("G7:stale-date-persist", {
      date: normalizedDate,
      decision: "pass",
      reason: null,
      flags: { explicitEdit: draft?.meta?.explicitEdit, explicitDelete: draft?.meta?.explicitDelete, source: draft?.meta?.source },
      exerciseCount: (draft.exercises || []).length,
      setCount: null,
    });

    const split = splitExercisesForApp(draft.exercises || [], draft.exerciseUnits || {});
    const draftToPersist = {
      ...draft,
      ...split,
    };

    // Bypass debounce for explicit deletes — write immediately so the state
    // survives a crash within the normal debounce window.
    if (draftToPersist.meta?.explicitDelete) {
      logWriteTrace("A-1:saveWorkoutDraft", {
        date: normalizedDate,
        decision: "executed",
        reason: "explicitDelete_bypass",
        flags: { explicitEdit: draftToPersist?.meta?.explicitEdit, explicitDelete: draftToPersist?.meta?.explicitDelete, source: draftToPersist?.meta?.source },
        exerciseCount: (draftToPersist.exercises || []).length,
        setCount: Object.values(draftToPersist.logData || {}).reduce((s, sets) => s + (Array.isArray(sets) ? sets.length : 0), 0),
      });
      saveWorkoutDraft(normalizedDate, draftToPersist, { storage, logger, bodyWeightKg: bodyWeightKgRef.current });
      onDraftPersist?.(draftToPersist, { source: "useWorkoutLog", hash: draftHash });
      lastSavedHashRef.current = draftHash;
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      logWriteTrace("A-1:saveWorkoutDraft", {
        date: normalizedDate,
        decision: "executed",
        reason: null,
        flags: { explicitEdit: draftToPersist?.meta?.explicitEdit, explicitDelete: draftToPersist?.meta?.explicitDelete, source: draftToPersist?.meta?.source },
        exerciseCount: (draftToPersist.exercises || []).length,
        setCount: Object.values(draftToPersist.logData || {}).reduce((s, sets) => s + (Array.isArray(sets) ? sets.length : 0), 0),
      });
      saveWorkoutDraft(normalizedDate, draftToPersist, { storage, logger, bodyWeightKg: bodyWeightKgRef.current });
      onDraftPersist?.(draftToPersist, {
        source: "useWorkoutLog",
        hash: draftHash,
      });
      lastSavedHashRef.current = draftHash;
    }, debounceMs);

    return () => window.clearTimeout(timeoutId);
  }, [debounceMs, draft, draftHash, logger, normalizedDate, onDraftPersist, storage]);

  const persistMutation = useCallback((prevDraft, nextExercises, reason, { explicitDelete = false } = {}) => {
    const split = splitExercisesForApp(nextExercises, prevDraft.exerciseUnits || {});
    return withWorkoutDraftMeta(normalizedDate, {
      ...prevDraft,
      exercises: nextExercises,
      ...split,
    }, {
      source: reason,
      hasUnsavedChanges: true,
      // Propagate so saveWorkoutDraft's empty-overwrite guard can distinguish
      // user-initiated deletes (which produce empty content intentionally) from
      // system auto-saves (which must not wipe existing draft data).
      ...(explicitDelete ? { explicitDelete: true } : { explicitEdit: true }),
    });
  }, [normalizedDate]);

  const mutateExercises = useCallback((reason, updater, mutation = {}) => {
    setDraft((prevDraft) => {
      const beforeExercises = prevDraft.exercises || [];
      const afterExercises = updater(beforeExercises).map((exercise) =>
        makeExercise(exercise, {
          logData: prevDraft.logData || {},
          exerciseUnits: prevDraft.exerciseUnits || {},
          fallbackLabel: prevDraft.todayLabels?.[0] || "その他",
        })
      );
      const removedExerciseNames = getRemovedExerciseNames(beforeExercises, afterExercises);
      const beforeSetCount = getTotalSetCount(beforeExercises);
      const afterSetCount = getTotalSetCount(afterExercises);
      const explicitDelete = Boolean(mutation.explicitDelete || reason === "exercise_remove" || reason === "set_remove");

      logWriteTrace("mutateExercises", {
        date: normalizedDate,
        decision: "pass",
        reason,
        flags: { explicitEdit: !explicitDelete, explicitDelete, source: reason },
        exerciseCount: afterExercises.length,
        setCount: afterSetCount,
        extra: { beforeExerciseCount: beforeExercises.length, beforeSetCount, removedExerciseNames },
      });

      if (!explicitDelete && (removedExerciseNames.length || afterSetCount < beforeSetCount)) {
        logWriteTrace("G11:non-delete-regression", {
          date: normalizedDate,
          decision: "block",
          reason: removedExerciseNames.length
            ? "non-delete mutation removed exercises"
            : "non-delete mutation reduced set count",
          flags: { explicitEdit: false, explicitDelete: false, source: reason },
          exerciseCount: beforeExercises.length,
          setCount: beforeSetCount,
          extra: { removedExerciseNames, beforeSetCount, afterSetCount },
        });
        logger?.warn?.("[workout log] blocked non-delete regression", {
          action: "workout_restore_integrity_check",
          date: normalizedDate,
          previousDisplayedExerciseNames: getExerciseNames(beforeExercises),
          restoredExerciseNames: getExerciseNames(afterExercises),
          lostExerciseNames: removedExerciseNames,
          beforeSetCount,
          afterSetCount,
          appliedSource: null,
          rejectedSource: reason,
          reason: removedExerciseNames.length
            ? "non-delete mutation removed exercises"
            : "non-delete mutation reduced set count",
        });
        return prevDraft;
      }
      logWriteTrace("G11:non-delete-regression", {
        date: normalizedDate,
        decision: "pass",
        reason: null,
        flags: { explicitEdit: !explicitDelete, explicitDelete, source: reason },
        exerciseCount: afterExercises.length,
        setCount: afterSetCount,
      });

      const nextDraft = persistMutation(prevDraft, afterExercises, reason, { explicitDelete });
      const resolvedDetails = typeof mutation.details === "function"
        ? mutation.details({
          beforeExercises,
          afterExercises,
          prevDraft,
          nextDraft,
        })
        : mutation.details;
      lastMutationRef.current = {
        reason,
        explicitDelete,
        explicitEdit: !explicitDelete,
        details: resolvedDetails || null,
      };
      return nextDraft;
    });
  }, [logger, normalizedDate, persistMutation]);

  const addExercise = useCallback((exerciseInput, labelOverride, options = {}) => {
    const name = String(
      typeof exerciseInput === "string"
        ? exerciseInput
        : (exerciseInput?.name || exerciseInput?.exerciseName || "")
    ).trim();
    if (!name) return null;

    const label = labelOverride
      || (typeof exerciseInput === "object" ? (exerciseInput.bodyPart || exerciseInput.label) : "")
      || "その他";
    const nextExercise = makeExercise({
      ...(typeof exerciseInput === "object" ? exerciseInput : {}),
      id: (typeof exerciseInput === "object" && exerciseInput.id) || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name,
      label,
      bodyPart: label,
    }, {
      exerciseUnits: {},
      fallbackLabel: label,
    });

    mutateExercises(options.reason || "exercise_add", (exercises) => {
      const duplicate = exercises.some((existing) =>
        normalizeExerciseName(existing.name) === normalizeExerciseName(nextExercise.name)
      );
      if (debug) {
        logger?.log?.("[workout log] exercise add attempt", {
          action: "exercise_add_attempt",
          date: normalizedDate,
          exerciseName: nextExercise.name,
          bodyPart: nextExercise.bodyPart,
          beforeExerciseNames: getExerciseNames(exercises),
          afterExerciseNames: duplicate ? getExerciseNames(exercises) : getExerciseNames([...exercises, nextExercise]),
          added: !duplicate,
          blocked: duplicate,
          blockedReason: duplicate ? "duplicate exercise" : "",
          duplicateDetected: duplicate,
          filteredOut: false,
          removedExerciseNames: [],
        });
      }
      if (duplicate) return exercises;
      return [...exercises, nextExercise];
    }, {
      details: {
        exerciseName: name,
        bodyPart: label,
      },
    });

    return nextExercise;
  }, [debug, logger, mutateExercises, normalizedDate]);

  const removeExercise = useCallback((idOrName, maybeName) => {
    const isNameOnly = maybeName === undefined;
    const targetId = isNameOnly ? null : idOrName;
    const targetName = isNameOnly ? idOrName : maybeName;
    mutateExercises("exercise_delete", (exercises) =>
      exercises.filter((exercise) => {
        if (targetId !== null && exercise.id === targetId) return false;
        return normalizeExerciseName(exercise.name) !== normalizeExerciseName(targetName);
      }), {
        explicitDelete: true,
        details: { exerciseName: targetName },
      }
    );
  }, [mutateExercises]);

  const addSet = useCallback((exerciseInput) => {
    const exerciseName = String(exerciseInput?.name || exerciseInput || "").trim();
    if (!exerciseName) return;

    // draft.exerciseUnits を閉じ込める（deps に含めることで stale closure を防ぐ）。
    // この経路（currentSets.length === 0 のとき savedExerciseUnits を使う分岐）は
    // renderHook を使ったフックテストがなく、自動テストで保護されていない。
    const savedExerciseUnits = draft.exerciseUnits || {};
    mutateExercises("set_add", (exercises) =>
      exercises.map((exercise) => {
        if (normalizeExerciseName(exercise.name) !== normalizeExerciseName(exerciseName)) return exercise;
        const currentSets = exercise.sets || [];
        const defaultUnit = currentSets.length
          ? getSetWeightMode(currentSets[0], "kg")
          : getDefaultUnitForExercise(exerciseName, savedExerciseUnits);
        return {
          ...exercise,
          sets: [...currentSets, makeSet({}, defaultUnit)],
        };
      }), {
        details: { exerciseName },
      }
    );
  }, [mutateExercises, draft.exerciseUnits]);

  const removeSet = useCallback((exerciseInput, setIndex) => {
    const exerciseName = String(exerciseInput?.name || exerciseInput || "").trim();
    if (!exerciseName) return;

    mutateExercises("set_remove", (exercises) =>
      exercises.map((exercise) => {
        if (normalizeExerciseName(exercise.name) !== normalizeExerciseName(exerciseName)) return exercise;
        const currentSets = exercise.sets || [];
        return {
          ...exercise,
          sets: currentSets.filter((_, index) => index !== setIndex),
        };
      }), {
        details: { exerciseName, setIndex },
      }
    );
  }, [mutateExercises]);

  const setField = useCallback((exerciseInput, setIndex, field, value) => {
    const exerciseName = String(exerciseInput?.name || exerciseInput || "").trim();
    if (!exerciseName) return;

    const reason = field === "weight" ? "weight_change" : field === "reps" ? "reps_change" : "set_input_change";
    let editDetails = null;

    mutateExercises(reason, (exercises) =>
      exercises.map((exercise) => {
        if (normalizeExerciseName(exercise.name) !== normalizeExerciseName(exerciseName)) return exercise;
        const currentSets = exercise.sets || [];
        const beforeSet = currentSets[setIndex] || makeSet();
        const currentMode = getSetWeightMode(beforeSet, "kg");
        const nextSet = withWeightMode({
          ...beforeSet,
          [field]: value,
        }, currentMode);
        if (field !== "done") {
          nextSet.done = nextSet.weight === "BW"
            ? Boolean(nextSet.reps)
            : Boolean(nextSet.weight && nextSet.reps);
        }

        const nextSets = currentSets.map((set, index) => (index === setIndex ? nextSet : set));
        editDetails = {
          exerciseName,
          setIndex,
          field,
          beforeValue: beforeSet?.[field],
          afterValue: value,
          beforeSet,
          afterSet: nextSet,
        };
        return { ...exercise, sets: nextSets };
      }), {
        details: () => editDetails || {
          exerciseName,
          setIndex,
          field,
        },
      }
    );
  }, [mutateExercises]);

  const setWeightMode = useCallback((exerciseInput, setIndex, requestedMode) => {
    const exerciseName = String(exerciseInput?.name || exerciseInput || "").trim();
    if (!exerciseName) return;

    let editDetails = null;

    mutateExercises("unit_change", (exercises) =>
      exercises.map((exercise) => {
        if (normalizeExerciseName(exercise.name) !== normalizeExerciseName(exerciseName)) return exercise;

        const currentSets = exercise.sets || [];
        const target = currentSets[setIndex] || makeSet();
        const currentMode = getSetWeightMode(target, "kg");
        const nextMode = requestedMode
          ? normalizeWeightMode(requestedMode)
          : ({ kg: "lbs", lbs: "BW", BW: "kg" }[currentMode] || "kg");
        const beforeSet = { ...target };
        const beforeUnits = currentSets.map((set) => getDisplayUnitForMode(getSetWeightMode(set, "kg")));
        const propagatedToSetIndexes = [];
        const skippedManualSetIndexes = [];
        const nextSets = currentSets.map((set, index) => {
          if (index === setIndex) {
            return {
              ...updateSetForWeightMode(set, nextMode, currentMode),
              unitManuallyChanged: true,
            };
          }
          if (setIndex !== 0) return set;
          if (set?.unitManuallyChanged) {
            skippedManualSetIndexes.push(index);
            return set;
          }
          propagatedToSetIndexes.push(index);
          return {
            ...updateSetForWeightMode(set, nextMode, getSetWeightMode(set, "kg")),
            unitManuallyChanged: false,
          };
        });
        const nextSet = nextSets[setIndex] || updateSetForWeightMode(target, nextMode, currentMode);

        if (debug) {
          logger?.log?.("[workout log] unit change propagation", {
            action: "unit_change_propagation",
            exerciseName,
            changedSetIndex: setIndex,
            changedUnit: getDisplayUnitForMode(nextMode),
            propagatedToSetIndexes,
            skippedManualSetIndexes,
            beforeUnits,
            afterUnits: nextSets.map((set) => getDisplayUnitForMode(getSetWeightMode(set, "kg"))),
          });
        }

        editDetails = {
          exerciseName,
          setIndex,
          field: "unit",
          beforeValue: getDisplayUnitForMode(currentMode),
          afterValue: getDisplayUnitForMode(nextMode),
          beforeSet,
          afterSet: nextSet,
        };
        return { ...exercise, sets: nextSets };
      }), {
        details: () => editDetails || {
          exerciseName,
          setIndex,
          field: "unit",
        },
      }
    );
  }, [debug, logger, mutateExercises]);

  const reorderExercise = useCallback((fromIdx, toIdx) => {
    mutateExercises("exercise_reorder", (exercises) => {
      const next = [...exercises];
      const [moved] = next.splice(fromIdx, 1);
      if (!moved) return exercises;
      next.splice(toIdx, 0, moved);
      return next;
    });
  }, [mutateExercises]);

  const renameExercise = useCallback((id, newName) => {
    const trimmed = String(newName || "").trim();
    if (!trimmed) return;
    mutateExercises("exercise_rename", (exercises) =>
      exercises.map((exercise) =>
        exercise.id === id ? { ...exercise, name: trimmed } : exercise
      ), {
        details: { afterName: trimmed },
      }
    );
  }, [mutateExercises]);

  /**
   * Directly update draft.todayLabels without going through the hasUnsavedChanges
   * guard. Called before plan-day exercise mutations so that the next onDraftChange
   * callback carries the correct labels to the App's bridge.
   */
  const setTodayLabels = useCallback((labels) => {
    const normalized = Array.isArray(labels) ? labels : [];
    setDraft((prevDraft) => {
      if (JSON.stringify(prevDraft.todayLabels || []) === JSON.stringify(normalized)) return prevDraft;
      return withWorkoutDraftMeta(normalizedDate, {
        ...prevDraft,
        todayLabels: normalized,
      }, {
        source: "today_labels_set",
        hasUnsavedChanges: Boolean(prevDraft.meta?.hasUnsavedChanges),
      });
    });
    // Mark as pending so onDraftChange fires even if no exercise mutation follows.
    // Exercise mutations in the same batch will overwrite this with their own reason.
    lastMutationRef.current = { reason: "today_labels_set", explicitEdit: false, explicitDelete: false };
  }, [normalizedDate]);

  const api = useMemo(() => ({
    date: normalizedDate,
    draft,
    exercises: draft.exercises || [],
    logData: splitDraft.logData,
    sessionEx: splitDraft.sessionEx,
    exerciseUnits: splitDraft.exerciseUnits,
    addExercise,
    removeExercise,
    addSet,
    removeSet,
    setField,
    setWeightMode,
    reorderExercise,
    renameExercise,
    setTodayLabels,
  }), [
    addExercise,
    addSet,
    removeSet,
    draft,
    normalizedDate,
    removeExercise,
    renameExercise,
    reorderExercise,
    setField,
    setWeightMode,
    setTodayLabels,
    splitDraft,
  ]);

  return api;
}

export default useWorkoutLog;
